/**
 * Blacklist matching. A blacklisted term matches a whole tag, not a piece of one: "ai" hides posts tagged `ai` and no longer
 * every post tagged `long_hair` or `maid`. Rules:
 *  - case and spaces do not matter: "Long Hair", "long_hair" and "long hair" are the same tag
 *  - a namespace in front of the tag is ignored ("artist:foo" is matched by "foo", and by "artist:foo")
 *  - `*` is a wildcard: "futa*" matches `futanari`, "*_(artist)" matches any artist tag
 *  - a post whose "tag" is really a title with spaces (Erome, galleries) is matched word by word, so "ai" still hides a post
 *    titled "ai generated girl" but not one titled "long hair"
 */

export function normalizeBlackTerm(term: string): string {
  return term.trim().toLowerCase().replace(/\s+/g, "_")
}

const patternCache = new Map<string, RegExp | string>()

function compile(term: string): RegExp | string {
  const hit = patternCache.get(term)
  if (hit !== undefined) return hit
  const out = term.includes("*")
    ? new RegExp("^" + term.split("*").map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join(".*") + "$")
    : term
  if (patternCache.size > 500) patternCache.clear()
  patternCache.set(term, out)
  return out
}

function matches(term: string, candidate: string): boolean {
  const pattern = compile(term)
  return typeof pattern === "string" ? pattern === candidate : pattern.test(candidate)
}

/** Does this one blacklist term match this one post tag? */
export function termMatchesTag(rawTerm: string, rawTag: string): boolean {
  const term = normalizeBlackTerm(rawTerm)
  if (!term) return false
  const tag = rawTag.trim().toLowerCase()
  if (!tag) return false

  const candidates = new Set<string>()
  const whole = tag.replace(/\s+/g, "_")
  candidates.add(whole)
  const colon = whole.indexOf(":")
  if (colon > 0) candidates.add(whole.slice(colon + 1)) // "artist:foo" is also just "foo"
  if (/\s/.test(tag)) for (const word of tag.split(/\s+/)) if (word) candidates.add(word) // a title: each word counts

  for (const candidate of candidates) if (matches(term, candidate)) return true
  // a title also matches a multi-word term as a phrase: "long hair" hides "a long hair maid outfit"
  if (/\s/.test(tag) && term.includes("_") && !term.includes("*")) {
    return ` ${tag.replace(/\s+/g, " ")} `.includes(` ${term.replace(/_/g, " ")} `)
  }
  return false
}

/** Does any post tag match any of the terms? */
export function anyTermMatches(terms: readonly string[], postTags: readonly string[]): boolean {
  return terms.some((term) => postTags.some((tag) => termMatchesTag(term, tag)))
}

/** Does every term match some post tag? (A rule: all its tags must be present.) */
export function everyTermMatches(terms: readonly string[], postTags: readonly string[]): boolean {
  return terms.every((term) => postTags.some((tag) => termMatchesTag(term, tag)))
}
